import { useAuth } from "@/contexts/AuthContext";
import { Eye, X } from "lucide-react";

const ROLE_LABELS = {
    superadmin: "Super Admin",
    employee: "Employee",
    lead_technician: "Lead Technician",
    technician: "Technician",
    finance: "Finance",
    sales: "Sales",
};

/**
 * Yellow/cyan strip at the top of /admin shown while the superadmin is previewing
 * the UI as a different role. Click "Exit preview" to drop back to their real perms.
 *
 * Mounted unconditionally in Admin.jsx — renders nothing when not in preview mode.
 */
export default function PreviewBanner() {
    const { previewRole, setPreviewRole, isSuperadmin } = useAuth();
    if (!previewRole) return null;
    // Defence in depth: clear the preview if the current user isn't actually a superadmin
    // (shouldn't happen since only superadmins can enable it, but a stale sessionStorage
    // value would otherwise lock them out of their real permissions).
    if (!isSuperadmin) {
        setPreviewRole(null);
        return null;
    }
    return (
        <div
            data-testid="preview-banner"
            className="sticky top-0 z-40 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white shadow-lg"
        >
            <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-2.5 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2.5 text-[13px]">
                    <span className="w-7 h-7 rounded-full bg-white/20 grid place-items-center">
                        <Eye size={13} />
                    </span>
                    <span className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase opacity-80">View-as</span>
                    <span>You're previewing the UI as <strong>{ROLE_LABELS[previewRole] || previewRole}</strong>. Tabs and write actions are gated by that role's permissions.</span>
                </div>
                <button
                    onClick={() => setPreviewRole(null)}
                    data-testid="exit-preview"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-[12px] font-medium transition"
                >
                    <X size={13} /> Exit preview
                </button>
            </div>
        </div>
    );
}
